import React from 'react';
import { useDeletePool, useRespondToOffer } from '@/hooks/queries/usePoolQueries';
import { OfferCounterInfo, OfferState, OfferType } from '@/types/offer';
import { PoolDetail } from '@/types/pool';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';
import { ModalTitle } from '../ModalTitle';

export type ActionType =
  | 'acceptOffer'
  | 'rejectOffer'
  | 'acceptCounterOffer'
  | 'rejectCounterOffer'
  | 'closePool'
  | 'withdrawOffer'
  | 'withdrawCounterOffer';

type ConfirmActionModalProps = {
  pool: PoolDetail;
  counterOfferInfo: OfferCounterInfo | null;
  offerId?: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  actionType: ActionType;
};

const actionConfig: Record<
  ActionType,
  { title: string; message: string; confirmText: string; confirmVariant: 'primary' | 'danger' }
> = {
  acceptOffer: {
    title: 'Accept Offer',
    message: 'Are you sure you want to accept this offer? This action cannot be undone.',
    confirmText: 'Accept Offer',
    confirmVariant: 'primary',
  },
  rejectOffer: {
    title: 'Reject Offer',
    message: 'Are you sure you want to reject this offer? This action cannot be undone.',
    confirmText: 'Reject Offer',
    confirmVariant: 'danger',
  },
  acceptCounterOffer: {
    title: 'Accept Counter Offer',
    message: 'Are you sure you want to accept this counter offer? This action cannot be undone.',
    confirmText: 'Accept Counter Offer',
    confirmVariant: 'primary',
  },
  rejectCounterOffer: {
    title: 'Reject Counter Offer',
    message: 'Are you sure you want to reject this counter offer? This action cannot be undone.',
    confirmText: 'Reject Counter Offer',
    confirmVariant: 'danger',
  },
  closePool: {
    title: 'Close Pool',
    message: 'Are you sure you want to close this pool? This action cannot be undone.',
    confirmText: 'Close Pool',
    confirmVariant: 'danger',
  },
  withdrawOffer: {
    title: 'Withdraw Offer',
    message: 'Are you sure you want to withdraw this offer? This action cannot be undone.',
    confirmText: 'Withdraw Offer',
    confirmVariant: 'danger',
  },
  withdrawCounterOffer: {
    title: 'Withdraw Counter Offer',
    message: 'Are you sure you want to withdraw this counter offer? This action cannot be undone.',
    confirmText: 'Withdraw Counter Offer',
    confirmVariant: 'danger',
  },
};

export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  pool,
  counterOfferInfo,
  offerId,
  isOpen,
  onClose,
  onSuccess,
  actionType,
}) => {
  const config = actionConfig[actionType];
  const deletePoolMutation = useDeletePool();
  const respondToOfferMutation = useRespondToOffer();

  const isLoading = actionType === 'closePool' ? deletePoolMutation.isPending : respondToOfferMutation.isPending;
  const error =
    actionType === 'closePool'
      ? deletePoolMutation.error?.message || null
      : respondToOfferMutation.error?.message || null;

  const handleConfirm = async () => {
    try {
      switch (actionType) {
        case 'closePool':
          await deletePoolMutation.mutateAsync(pool.id);
          break;
        case 'acceptOffer':
          if (!offerId) return;
          await respondToOfferMutation.mutateAsync({
            pool_id: pool.id,
            offer_id: offerId,
            type: OfferType.OFFER,
            state: OfferState.ACCEPTED,
            counter_offer_info: null,
          });
          break;
        case 'rejectOffer':
          if (!offerId) return;
          await respondToOfferMutation.mutateAsync({
            pool_id: pool.id,
            offer_id: offerId,
            type: OfferType.OFFER,
            state: OfferState.REJECTED,
            counter_offer_info: null,
          });
          break;
        case 'acceptCounterOffer':
          if (!offerId) return;
          if (!counterOfferInfo) return;
          await respondToOfferMutation.mutateAsync({
            pool_id: pool.id,
            offer_id: offerId,
            type: OfferType.COUNTER_OFFER,
            state: OfferState.ACCEPTED,
            counter_offer_info: counterOfferInfo,
          });
          break;
        case 'rejectCounterOffer':
          if (!offerId) return;
          if (!counterOfferInfo) return;
          await respondToOfferMutation.mutateAsync({
            pool_id: pool.id,
            offer_id: offerId,
            type: OfferType.COUNTER_OFFER,
            state: OfferState.REJECTED,
            counter_offer_info: counterOfferInfo,
          });
          break;
        case 'withdrawOffer':
          if (!offerId) return;
          await respondToOfferMutation.mutateAsync({
            pool_id: pool.id,
            offer_id: offerId,
            type: OfferType.OFFER,
            state: OfferState.CANCELED,
            counter_offer_info: null,
          });
          break;
        case 'withdrawCounterOffer':
          if (!offerId) return;
          if (!counterOfferInfo) return;
          await respondToOfferMutation.mutateAsync({
            pool_id: pool.id,
            offer_id: offerId,
            type: OfferType.COUNTER_OFFER,
            state: OfferState.CANCELED,
            counter_offer_info: counterOfferInfo,
          });
          break;
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Action failed:', error);
      // Error is already handled by the mutation state
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={400} header={<ModalTitle title={config.title} />}>
      <div className="p-8 flex flex-col gap-6">
        <p className="text-gray-500 leading-relaxed">{config.message}</p>

        {error && <div className="bg-red-50 border border-red-200 text-[#DC2626] p-3 rounded-lg text-sm">{error}</div>}

        <div className="flex gap-4 justify-end mt-10">
          <Button onClick={onClose} variant="secondary" disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant={config.confirmVariant} disabled={isLoading} loading={isLoading}>
            {isLoading ? 'Processing...' : config.confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
